import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import gemsPackUrl from '#src/assets/gems/gems.webp';
import { Attribute } from '#src/components/buyActions/PaymentBox/CardOverview/index.tsx';
import { BundledPurchasableRewards } from '#src/components/commonCard/BuyPacks/BundledPurchasableRewards/index.tsx';

import type { TokenPreview_purchasableItem } from './__generated__/index.graphql.ts';

const backgroundCss = css`
  --square-width: var(--double-and-a-half-unit);
  background:
    linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%),
    repeating-linear-gradient(
      to left,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    repeating-linear-gradient(
      to bottom,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    linear-gradient(180deg, #284b30 0%, rgba(0, 0, 0, 0.7) 100%);
`;

const Root = styled(Horizontal).attrs({ gap: 2, center: true })`
  padding: var(--double-unit);
  ${backgroundCss}
  img {
    max-width: 120px;
  }

  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    --square-width: calc(var(--unit) * 5);
    img {
      max-width: none;
    }
  }
`;

const Picture = styled(Img)`
  width: calc(45 * var(--unit));
  object-fit: contain;
`;

const AttributeWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  border-radius: var(--double-unit);
  background-color: var(--c-nd-100);
`;

const Value = styled(AttributeWrapper)`
  padding: var(--half-unit) var(--unit);
  img {
    width: 24px;
  }
`;

type Props = {
  purchasableItem: TokenPreview_purchasableItem;
};

export const TokenPreview = ({ purchasableItem }: Props) => {
  if (isType(purchasableItem, 'InGameCurrencyPurchasableItem')) {
    const { pictureUrl, baseAmount, bonusAmount } = purchasableItem;
    return (
      <Root>
        <Picture src={pictureUrl || gemsPackUrl} alt="" />
        <Vertical gap={0.5} center>
          <Attribute
            label={<FormattedMessage defaultMessage="Bundle of Gems" />}
            value={
              <Value>
                +{baseAmount} <GemIcon />
              </Value>
            }
          />
          {bonusAmount > 0 && (
            <Attribute
              label={<FormattedMessage defaultMessage="Bonus" />}
              value={
                <Value>
                  +{bonusAmount} <GemIcon />
                </Value>
              }
            />
          )}
        </Vertical>
      </Root>
    );
  }
  if (isType(purchasableItem, 'BundledPurchasableItem')) {
    return (
      <Root>
        <Picture src={purchasableItem.pictureUrl || gemsPackUrl} alt="" />
        <BundledPurchasableRewards purchasableItem={purchasableItem} />
      </Root>
    );
  }
  return null;
};

TokenPreview.fragments = {
  purchasableItem: gql`
    fragment TokenPreview_purchasableItem on PurchasableItemInterface {
      __typename
      slug
      pictureUrl
      ... on InGameCurrencyPurchasableItem {
        slug
        baseAmount
        bonusAmount
      }
      ...BundledPurchasableRewards_purchasableItem
    }
    ${BundledPurchasableRewards.fragments.purchasableItem}
  ` as TypedDocumentNode<TokenPreview_purchasableItem>,
};
